/**
 * A3 — Piezas comunes de la comunicación omnicanal: iconos y etiquetas de canal y
 * sentido, y la carga de los canales del bufete.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  ArrowDownLeft,
  ArrowLeftRight,
  ArrowUpRight,
  Building2,
  FileText,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Phone,
  Users,
} from 'lucide-react'
import { api } from '../../lib/api'
import {
  CommunicationDirectionLabel,
  type CommunicationChannelKind,
  type CommunicationChannelListResponse,
  type CommunicationDirection,
} from '@toga/shared'

export const CHANNEL_ICON: Record<CommunicationChannelKind, ReactNode> = {
  EMAIL: <Mail size={14} />,
  PHONE: <Phone size={14} />,
  MEETING: <Users size={14} />,
  WHATSAPP: <MessageCircle size={14} />,
  LETTER: <FileText size={14} />,
  VISIT: <Building2 size={14} />,
  OTHER: <MoreHorizontal size={14} />,
}

const DIRECTION_ICON: Record<CommunicationDirection, ReactNode> = {
  INBOUND: <ArrowDownLeft size={11} />,
  OUTBOUND: <ArrowUpRight size={11} />,
  BOTH: <ArrowLeftRight size={11} />,
}

export function DirectionBadge({ direction }: { direction: CommunicationDirection }) {
  const cls =
    direction === 'INBOUND'
      ? 'border-sky-500/30 text-sky-300 bg-sky-500/10'
      : direction === 'OUTBOUND'
        ? 'border-emerald-500/30 text-emerald-300 bg-emerald-500/10'
        : 'border-base t-muted'
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${cls}`}
    >
      {DIRECTION_ICON[direction]}
      {CommunicationDirectionLabel[direction]}
    </span>
  )
}

/** Canales del bufete (activados o no) tal como los devuelve la API. */
export function useChannels() {
  const [data, setData] = useState<CommunicationChannelListResponse | null>(null)
  useEffect(() => {
    let alive = true
    api
      .get<CommunicationChannelListResponse>('/communication-channels')
      .then((r) => alive && setData(r))
      .catch(() => alive && setData(null))
    return () => {
      alive = false
    }
  }, [])
  return data
}

export const inputCls =
  'w-full bg-transparent border border-base rounded-lg px-3 py-1.5 text-sm t-primary placeholder:t-faint focus:outline-none focus:border-toga-500/50'

/** Solo dígitos (con prefijo 34 si es un móvil español de 9 cifras) para wa.me. */
export function whatsappNumber(raw: string | null | undefined): string | null {
  const digits = (raw ?? '').replace(/\D/g, '').replace(/^00/, '')
  if (digits.length === 9) return `34${digits}`
  return digits.length >= 10 ? digits : null
}

/** Enlace que abre WhatsApp con el mensaje escrito; lo envía la persona. */
export function whatsappLink(phone: string | null | undefined, text: string): string | null {
  const n = whatsappNumber(phone)
  return n ? `https://wa.me/${n}?text=${encodeURIComponent(text)}` : null
}
